import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Modal } from '@skupreme/shared/ui';
import { FormPasswordField, FormTextField } from '@skupreme/shared/ui/form';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { contactHelpFulfillmentLink } from '@utility/constants/common';
import StorageProviderService from '@utility/services/storage-provider';
import { ToastMessage } from '@utility/toast';

const credentialSchema = z.object({
  email_address: z
    .string()
    .min(1, 'Email Address is required field')
    .email('Please enter valid e-mail'),
  password: z.string().min(1, 'Password is required field'),
});

type CredentialValues = z.infer<typeof credentialSchema>;

const PROVIDER = {
  name: 'Tactical Logistics',
  link: contactHelpFulfillmentLink.tacticalLogistics,
};

type CredentialPopupProps = {
  open: boolean;
  /** Which provider's credentials; Tactical is the only one today. */
  type?: string;
  onClose: () => void;
  providerData?: {
    _id?: string;
    tactical_credentials?: { tactical_3plnext_credentials?: unknown };
  };
  refresh: () => void;
};

export function CredentialPopup({
  open,
  onClose,
  providerData,
  refresh,
}: CredentialPopupProps) {
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit, reset } = useForm<CredentialValues>({
    resolver: zodResolver(credentialSchema),
    defaultValues: { email_address: '', password: '' },
  });

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const updateCredentials = async (formValues: CredentialValues) => {
    try {
      setLoading(true);
      await StorageProviderService.tacticalAuth({
        email: formValues.email_address,
        password: formValues.password,
        fulfillmentProviderId: providerData?._id,
        legacy:
          !!providerData?.tactical_credentials?.tactical_3plnext_credentials,
      });
      refresh();
      onClose();

      ToastMessage.success('Credentials updated successfully');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      size="md"
      isOpen={open}
      onOpenChange={isOpen => !isOpen && onClose()}
      isDismissable={!loading}
      title={`Set ${PROVIDER.name} Credentials`}
      showCloseButton
      footer={
        <>
          <Button variant="ghost" onPress={onClose} isDisabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            isPending={loading}
            onPress={() => handleSubmit(updateCredentials)()}
          >
            Confirm
          </Button>
        </>
      }
    >
      <p className="sk:m-0 sk:mb-4 sk:text-dense sk:text-fg-secondary">
        Please contact{' '}
        <a href={PROVIDER.link} target="_blank" rel="noreferrer">
          {PROVIDER.name}
        </a>{' '}
        for access or to set up your 3PL.
      </p>
      <form
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit(updateCredentials)}
        className="sk:flex sk:flex-col sk:gap-4"
      >
        <FormTextField
          control={control}
          name="email_address"
          label="Email Address"
          isRequired
        />
        <FormPasswordField
          control={control}
          name="password"
          label="Password"
          autoComplete="current-password"
          isRequired
        />
        {/* Enter in a field submits, as it did before. */}
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
